// FundsUnitsSection — the Owner Contact form's "Funds & year-end taxes" body for
// the married-filing-jointly model (docs/appfolio-owner-vendor-sync-spec.md §13).
// PUBLIC, owner-facing: every string is // Copy approved by Miles 2026-09-25 (the copy
// lives in lib/ownerContactFunds.ts, or inline below where marked).
//
//   1. Married and filing jointly?  'yes_no' (two individuals) or 'pairs' (more —
//      the owner picks the couples; each person in at most one).
//   2. Each couple chooses who receives its funds + the year-end 1099.
//   3. Two or more tax units → funds follow ownership: ownership % per person, and
//      each unit's funds share READ-ONLY, as the server derived it.
//      One unit (a couple) → just the designee, plus the optional account label.
//
// There is no "one of us handles it" choice: between people who are not a
// married couple, funds follow ownership. The server derives the tax units
// (applicable.taxUnits) from the saved answers; this renders them and records
// the owner's clicks as `answers.spouses`. Names only — never an id.

import { useState } from 'react';
import { radioTileStyle, TextField } from '@/components/ownerContact/formBits';
import type {
  OwnerContactAnswers,
  OwnerContactApplicable,
  OwnerContactSpouses,
} from '@/lib/ownerContactClient';
import {
  canAddCouple,
  coupleChoices,
  coupleDesigneeQuestion,
  draftFromSpouses,
  FUNDS_FOLLOW_OWNERSHIP_HELPER,
  individualsOf,
  ownershipSum,
  personName,
  spousesChoice,
  spousesFromDraft,
  spousesHelper,
  spousesQuestionText,
  spousesSettled,
  unitFundsLine,
  withCoupleDesignee,
  yesNoSpouses,
  type CoupleDraft,
} from '@/lib/ownerContactFunds';

function parsePercent(s: string): number | undefined {
  const t = s.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

const PERCENT_EPSILON = 0.01;

export function FundsUnitsSection({
  applicable,
  answers,
  onSpousesChange,
  onOwnershipChange,
  onOwnershipBlur,
  onToggle,
  onAccountLabelChange,
  onAccountLabelBlur,
}: {
  applicable: OwnerContactApplicable;
  answers: OwnerContactAnswers;
  /** Save the spouses answer (undefined = not answered yet) and refresh the units. */
  onSpousesChange: (spouses: OwnerContactSpouses | undefined) => void;
  onOwnershipChange: (ownerIndex: number, value: number | undefined) => void;
  onOwnershipBlur: () => void;
  onToggle: (ownerIndex: number, key: 'receivesStatements' | 'receivesEmail', value: boolean) => void;
  onAccountLabelChange: (v: string) => void;
  onAccountLabelBlur: () => void;
}): React.JSX.Element {
  const question = applicable.spousesQuestion ?? 'none';
  const count = applicable.ownerCount;
  const individuals = individualsOf(applicable.perOwner);
  const nameOf = (i: number) => personName(applicable.perOwner, i);
  const saved = answers.spouses;
  const choice = spousesChoice(saved);

  // 'pairs': "yes" is a local choice until at least one couple is complete (a
  // saved `couples: []` would read as "no").
  const [pairsYes, setPairsYes] = useState<boolean>(choice === 'yes');
  const [draft, setDraft] = useState<CoupleDraft[]>(() => {
    const d = draftFromSpouses(saved);
    return d.length > 0 ? d : [{ a: null, b: null }];
  });

  const updateDraft = (next: CoupleDraft[]) => {
    setDraft(next);
    onSpousesChange(spousesFromDraft(next, saved));
  };

  const settled = spousesSettled(applicable, answers) && !(question === 'pairs' && pairsYes && choice !== 'yes');
  const units = applicable.taxUnits ?? [];
  const coupleUnits = units.filter((u) => u.kind === 'couple' && u.members.length === 2);
  const fundsFollow = applicable.fundsFollowOwnership === true;
  const oneCouple = units.length === 1 && coupleUnits.length === 1;
  const everyone = applicable.perOwner.map((p) => p.ownerIndex);
  const sum = ownershipSum(answers.owners, everyone);
  const sumOk = Math.abs(sum - 100) <= PERCENT_EPSILON;

  /** The designee the owner picked (local answers first — the server echo can lag a click). */
  const designeeFor = (members: number[]): number | null => {
    const [a, b] = members;
    const c = saved?.couples.find((x) => (x.a === a && x.b === b) || (x.a === b && x.b === a));
    return c?.designee ?? null;
  };

  const yesNoTiles = (
    name: string,
    selected: 'yes' | 'no' | null,
    onPick: (v: 'yes' | 'no') => void,
  ): React.JSX.Element => (
    <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
      {(['yes', 'no'] as const).map((v) => (
        <label key={v} style={{ ...radioTileStyle(selected === v), flex: '1 1 8rem' }}>
          <input type="radio" name={name} checked={selected === v} onChange={() => onPick(v)} />
          <span style={{ color: 'var(--fg)' }}>{v === 'yes' ? 'Yes' : 'No'}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {/* 1 — Married and filing jointly? */}
      {question === 'yes_no' && (
        <div className="field" style={{ margin: 0 }}>
          <label>{spousesQuestionText('yes_no', individuals, count)}</label>
          {yesNoTiles('oc-spouses', choice, (v) => onSpousesChange(yesNoSpouses(v, individuals, saved)))}
          <span className="helper">{spousesHelper('yes_no', count)}</span>
        </div>
      )}

      {question === 'pairs' && (
        <div className="field" style={{ margin: 0 }}>
          <label>{spousesQuestionText('pairs', individuals, count)}</label>
          {yesNoTiles('oc-spouses', pairsYes ? 'yes' : choice === 'no' ? 'no' : null, (v) => {
            if (v === 'yes') {
              setPairsYes(true);
              onSpousesChange(spousesFromDraft(draft, saved));
            } else {
              setPairsYes(false);
              onSpousesChange({ answered: true, couples: [] });
            }
          })}
          <span className="helper">{spousesHelper('pairs', count)}</span>

          {pairsYes && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
              <span style={{ fontWeight: 600, color: 'var(--fg)' }}>Which owners are married to each other?</span>
              {draft.map((row, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  {(['a', 'b'] as const).map((side, j) => (
                    <span key={side} style={{ display: 'contents' }}>
                      {j === 1 && <span style={{ color: 'var(--fg-muted)' }}>and</span>}
                      <select
                        className="input"
                        style={{ flex: '1 1 10rem' }}
                        aria-label={`Couple ${i + 1}, ${side === 'a' ? 'first' : 'second'} spouse`}
                        value={row[side] ?? ''}
                        onChange={(e) => {
                          const v = e.target.value === '' ? null : Number(e.target.value);
                          updateDraft(draft.map((d, k) => (k === i ? { ...d, [side]: v } : d)));
                        }}
                      >
                        <option value="">Choose an owner</option>
                        {coupleChoices(draft, i, side, individuals).map((p) => (
                          <option key={p.index} value={p.index}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </span>
                  ))}
                  {draft.length > 1 && (
                    <button
                      type="button"
                      className="btn"
                      data-variant="ghost"
                      data-size="sm"
                      onClick={() => updateDraft(draft.filter((_, k) => k !== i))}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {canAddCouple(draft, individuals) && (
                <button
                  type="button"
                  className="btn"
                  data-variant="link"
                  data-size="sm"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setDraft([...draft, { a: null, b: null }])}
                >
                  + Add another married couple
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* 2 — Each couple: who receives the funds + 1099. */}
      {settled &&
        coupleUnits.map((u) => {
          const [a, b] = u.members as [number, number];
          const chosen = designeeFor(u.members);
          return (
            <div key={`${a}-${b}`} className="field" style={{ margin: 0 }}>
              <label>{coupleDesigneeQuestion(nameOf(a), nameOf(b))}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {[a, b].map((idx) => (
                  <label key={idx} style={radioTileStyle(chosen === idx)}>
                    <input
                      type="radio"
                      name={`oc-couple-${a}-${b}`}
                      checked={chosen === idx}
                      onChange={() => saved && onSpousesChange(withCoupleDesignee(saved, u.members, idx))}
                    />
                    <span style={{ color: 'var(--fg)' }}>{nameOf(idx)}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

      {/* 3a — One couple: the designee is the whole answer, plus the optional label. */}
      {settled && oneCouple && (
        <TextField
          label="What should the check or deposit reference? (optional)"
          value={answers.disbursement?.fundsAccountLabel ?? ''}
          placeholder="e.g. joint checking"
          onChange={onAccountLabelChange}
          onBlur={onAccountLabelBlur}
        />
      )}

      {/* 3b — Two or more units: funds follow ownership. */}
      {settled && fundsFollow && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p className="helper" style={{ margin: 0, color: 'var(--fg-muted)' }}>
            {FUNDS_FOLLOW_OWNERSHIP_HELPER}
          </p>
          {applicable.perOwner.map((po) => {
            const oa = answers.owners?.[po.ownerIndex];
            return (
              <div
                key={po.role}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--fg)', marginBottom: 'var(--space-3)' }}>
                  {nameOf(po.ownerIndex)}
                </div>
                <div className="field" style={{ margin: 0, maxWidth: 200 }}>
                  <label>Ownership %</label>
                  <input
                    className="input"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={100}
                    value={oa?.ownershipPercent ?? ''}
                    onChange={(e) => onOwnershipChange(po.ownerIndex, parsePercent(e.target.value))}
                    onBlur={onOwnershipBlur}
                  />
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <input
                      type="checkbox"
                      checked={oa?.receivesStatements !== false}
                      onChange={(e) => onToggle(po.ownerIndex, 'receivesStatements', e.target.checked)}
                    />
                    <span className="helper" style={{ color: 'var(--fg-muted)' }}>
                      Receives owner statements
                    </span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <input
                      type="checkbox"
                      checked={oa?.receivesEmail !== false}
                      onChange={(e) => onToggle(po.ownerIndex, 'receivesEmail', e.target.checked)}
                    />
                    <span className="helper" style={{ color: 'var(--fg-muted)' }}>
                      Receives email
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
          <div className="helper" style={{ color: sumOk ? 'var(--fg-muted)' : 'var(--danger)' }}>
            Ownership adds to {sum}% — it needs to total 100%.
          </div>
          <ul
            style={{ margin: 0, paddingLeft: 'var(--space-5)', color: 'var(--fg)' }}
            aria-label="Where the funds go"
            data-testid="funds-follow-ownership"
          >
            {units.map((u) => (
              <li key={u.members.join('-')}>{unitFundsLine({ ...u, designee: u.kind === 'couple' ? designeeFor(u.members) ?? u.designee : u.designee }, nameOf)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
